import React, { FormEvent, useState } from 'react';
import Complaint from '../../../models/Complaint';
import Pagination from '../../../models/Pagination';
import { Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Paper, darken, Typography, Badge, Stack, TableContainer, IconButton, Tooltip, Collapse, alpha } from '@mui/material';
import PaginationButtons from '../../PaginationButtons';
import { ISTableTitle, ISTableHeaderCell, ISTableRow, ISTableButton, ISTableCell } from '../../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../theme/ThemeContext';
import moment from 'moment';
import { ArrowDropDownOutlined, ArrowDropUpOutlined, AttachFile } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { downloadComplaintMessageFile } from '../../../actions/complaint/complaintMessageDownloadFile';

export const complaintStatusTypes = ['U', 'R']

export type ComplaintStatusType = typeof complaintStatusTypes[number]

export const complaintStatusTypeToDescription = (type: ComplaintStatusType): string => {
  switch (type) {
    case 'U': return "unread"
    case 'R': return "inProgress"
    case 'O': return "other"
  }
  return "not"
}

export const complaintMessageStatusTypeToDescription = (type: ComplaintStatusType): string => {
  switch (type) {
    case 'U': return "unread"
    case 'R': return "read"
    case 'O': return "other"
  }
  return "not"
}


export const complaintAssignedTo = ['N', 'M', 'O']

export type ComplaintAssignedTo = typeof complaintAssignedTo[number]

export const complaintAssignedToDescription = (type: ComplaintAssignedTo): string => {
  switch (type) {
    case 'N': return "notAssigned"
    case 'M': return "toMe"
    case 'O': return "toOthers"
  }
  return "not"
}

export const complaintInternalAssignedTo = ['N', 'M']

export type ComplaintInternalAssignedTo = typeof complaintInternalAssignedTo[number]

export const complaintInternalAssignedToDescription = (type: ComplaintInternalAssignedTo): string => {
  switch (type) {
    case 'N': return "notAssigned"
    case 'M': return "toMe"
  }
  return "not"
}

export const complaintOwnerOpenBy = ['M', 'O']

export type ComplaintOwnerOpenBy = typeof complaintOwnerOpenBy[number]

export const complaintOwnerOpenByDescription = (type: ComplaintOwnerOpenBy): string => {
  switch (type) {
    case 'M': return "byMe"
    case 'O': return "byOthers"
  }
  return "not"
}

export const complaintCloseReasons = ['V', 'P', 'N', 'C', 'O']

export type ComplaintCloseReason = typeof complaintCloseReasons[number]

export const complaintCloseReasonToDescription = (type: ComplaintCloseReason): string => {
  switch (type) {
    case 'V': return "valid"
    case 'P': return "partialValid"
    case 'N': return "notValid"
    case 'C': return "cancelled"
    case 'O': return "other"
  }
  return "not"
}

export interface Row {
  data: Complaint
  action?: 'create' | 'update' | 'error';
}

interface Props {
  rows: Row[];
  loading: boolean;
  ownerId?: number;
  pagination?: Pagination;
  fromIn?: boolean;
  adminInfo?: boolean;
  managerInfo?: boolean;
  fullInfo?: boolean;
  clientInfo?: boolean;
  staffInfo?: boolean;
  isClient?: boolean;
  isChaco?: boolean;
  onClickInfo?: (complaint: Complaint) => void;
  onClickTracking?: (complaint: Complaint) => void;
  onClickInternalTracking?: (complaint: Complaint) => void;
  onChangeData?: (complaint: Complaint, status: boolean, assignedTo: boolean, internalAssignedTo: boolean) => void;
  onChangePage?: (page: number) => void;
}

const Complaints: React.FC<Props> = (props) => {
  const { t } = useTranslation('complaint')
  const { theme } = useThemeContext()
  const [showFullInfo, setShowFullInfo] = useState<boolean>(!props.clientInfo)
  const dispatch = useDispatch()
  const [downloading, setDownloading] = useState<string>('')

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      default: return 'default';
    }
  }

  const downloadFileComplaintMessage = async (complaint: Complaint, fileId: string, fileName: string) => {
    if (complaint.fileId_1 && complaint.id) {
      try {
        setDownloading(`${complaint.id}${fileId}`)
        await dispatch(downloadComplaintMessageFile(complaint.id, fileId, undefined,
          (file: any) => {

            const url = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }))
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
              'download',
              fileName ? fileName : `${complaint.message}.pdf`
            );
            document.body.appendChild(link);
            // Start download
            link.click();

            URL.revokeObjectURL(url);
          }))
        setDownloading('')
      }
      catch {
      }
    }
  }

  const rowsToShow = props.rows.filter(row => !props.staffInfo || (props.staffInfo &&
    ((row.data.ownerOpen.isChaco && props.isChaco ) || (!row.data.ownerOpen.isChaco && !props.isChaco )) &&
    (row.data.visibleCategory || (!row.data.visibleCategory && (row.data.internalAssignedToId === -1 || row.data.internalAssignedToId === props.ownerId)))))

  const otherTooltip = (text: string) => <Paper elevation={2} sx={{ padding: "10px" }}>
    <Typography >{text}</Typography>
  </Paper>

  /*
    const internalStateBgColor = (r: Row) => {
      if (r.data.assignedToId &&
        props.adminInfo &&
        !props.staffInfo && !r.data.visibleCategory) {
        if (r.data.internalDateClose)
          return darkModeEnabled ? alpha(theme.palette.info.light, 0.3) : alpha(theme.palette.info.light, 0.5) // violeta
        if (!r.data.internalAssignedToId)
          return alpha(theme.palette.info.dark, 0.2) // celeste
        if (r.data.internalAssignedToId && r.data.internalAssignedToId !== -1)
          return alpha(theme.palette.warning.main, 0.1) // naranja
        if (r.data.internalAssignedToId === -1)
          return alpha(theme.palette.error.main, 0.1) // rojo
      }
      return ''
    }
  
    Administración de ventas lo asigno a Logística (sin importar si algún usuario de logística ya se lo autoasigno o no).
    Alguna tonalidad naranja/marrón como de los soportes ej 75/77/80
    
    *Logística ya dio alguna condición de cierre.
    Alguna tonalidad gris azulado como de los soportes ej 68/69/73, o verde.
    
    Para el resto de los casos quedaría sin color de fondo, o gris oscuro cuando están cerrados.
    
  */

  const internalStateBgColor = (r: Row) => {
    if (r.data.assignedToId &&
      props.adminInfo 
      //&& !props.staffInfo && !r.data.visibleCategory
      ) 
      {
      if (r.data.internalDateClose)
        return alpha(theme.palette.info.dark, 0.2) // celeste
      else {
        if (r.data.internalAssignedToId)
          return alpha(theme.palette.warning.main, 0.1) // naranja
      }
    }
    return ''
  }

  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', maxHeight: props.fullInfo ? '80%' : '100%', borderRadius: "10px", margin: props.fromIn ? "-10px" : "10px", minWidth: '770px' }}
      onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); props.clientInfo ? setShowFullInfo(!showFullInfo) : setShowFullInfo(true) }}>

      <ISTableTitle title={!props.fullInfo ? t('lastRecords') : t('title')}
        sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}> {/*,  }}>*/}
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          {props.clientInfo && showFullInfo ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
            props.clientInfo ? <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> : <></>}
        </Stack>
      </ISTableTitle>
      <Collapse style={{ padding: props.clientInfo ? "10px" : 'inherit' }} in={props.clientInfo ? showFullInfo : true}>
        <TableContainer sx={{ overflowX: 'auto', height: !props.fullInfo ? '250px' : '100%' }}>
          <Table style={{ whiteSpace: "nowrap", overflow: "hidden" }} size="medium" stickyHeader={props.fullInfo} >
            <TableHead>
              <TableRow selected={true}>
                <ISTableHeaderCell align="center">{t('dateOpen')}</ISTableHeaderCell>
                <ISTableHeaderCell align="center">{t('id')}</ISTableHeaderCell>
                {props.fullInfo && <ISTableHeaderCell>{t('category')}</ISTableHeaderCell>}
                <ISTableHeaderCell>{t('type')}</ISTableHeaderCell>
                <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
                <ISTableHeaderCell align="center" >{t('status')}</ISTableHeaderCell>
                {props.fullInfo && <ISTableHeaderCell align="center" ></ISTableHeaderCell>}
                {props.fullInfo && <ISTableHeaderCell align="center">{t('dateClose')}</ISTableHeaderCell>}
                {props.adminInfo && <ISTableHeaderCell>{t('assignedToName')}</ISTableHeaderCell>}
                {props.adminInfo && <ISTableHeaderCell>{t('personType')}</ISTableHeaderCell>}
                {props.fullInfo && <ISTableHeaderCell align='center'>{t('tracking')}</ISTableHeaderCell>}
                {!props.isChaco && props.fullInfo && props.adminInfo && <ISTableHeaderCell align='center'>{t('internalTracking')}</ISTableHeaderCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {!props.loading && rowsToShow.map((row, index) => {
                const unreadMessages = props.ownerId && row.data.messages && row.data.messages.length > 0 ? row.data.messages.filter(cm => cm.ownerId !== props.ownerId && cm.status === 'U').length : 0
                const unreadInternalMessages = props.adminInfo && props.ownerId && row.data.internalMessages && row.data.internalMessages.length > 0 ? row.data.internalMessages.filter(cm => cm.ownerId !== props.ownerId && cm.status === 'U').length : 0
                const tooltipTitle =
                  <Paper elevation={2} sx={{ padding: "10px" }}>
                    <Typography >{row.data.assignedToName && `${t('assignedToName')} ${row.data.assignedToName}`}</Typography>
                    <Typography>{row.data.ownerOpenName && `${t('ownerOpenName')} ${row.data.ownerOpenName}`}</Typography>
                    <Typography>{row.data.ownerCloseName && `${t('ownerCloseName')} ${row.data.ownerCloseName}`}</Typography>
                    <Typography >{row.data.internalAssignedToId === -1 && props.adminInfo && `${t('waitingInternalAssignedTo')}`}</Typography>
                    <Typography >{row.data.internalAssignedToName && props.adminInfo && `${t('internalAssignedToName')} ${row.data.internalAssignedToName}`}</Typography>
                    {row.data.internalCloseReason && props.adminInfo && <Typography >{`${t('internalCloseReason')} ${t(complaintCloseReasonToDescription(row.data.internalCloseReason))} - ${moment(new Date(row.data.internalDateClose!)).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}`}</Typography>}
                  </Paper>
                const tooltipText = (text: string) =>
                  <Paper elevation={2} sx={{ padding: "10px" }}>
                    <Typography >{text}</Typography>
                  </Paper>
                const fontStyle = { fontWeight: row.data.status === 'U' || unreadMessages > 0 || unreadInternalMessages > 0 ? 'bold' : 'default' }
                return (
                  <ISTableRow key={index} color={mapActionToColor(row.action)}
                    sx={{ cursor: props.isClient ? 'pointer' : 'default', backgroundColor: row.data.dateClose ? darken(theme.palette.action.hover, 0.1) : internalStateBgColor(row) }}
                    onClick={(e) => {
                      e.stopPropagation();
                      //props.adminInfo ? props.onClickInfo && props.onClickInfo(row.data) : 

                      // se saca la opcion de entrar clickeando sobre la linea cuando no es cliente
                      props.isClient && props.onClickTracking && props.onClickTracking(row.data)
                    }}>
                    <ISTableCell align="center" tooltip={tooltipTitle} style={fontStyle}>{moment(new Date(row.data.dateOpen)).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</ISTableCell>
                    <ISTableCell align="center" tooltip={tooltipTitle} style={fontStyle}>{row.data.id}</ISTableCell>
                    {props.fullInfo && <ISTableCell tooltip={otherTooltip(row.data.category)}
                      style={{ ...fontStyle, maxWidth: props.fullInfo ? '80px' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.data.category}</ISTableCell>}
                    <ISTableCell tooltip={otherTooltip(row.data.type)} style={{ ...fontStyle, maxWidth: props.fullInfo ? '180px' : '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.data.type}</ISTableCell>
                    <ISTableCell tooltip={otherTooltip(row.data.message)} style={{ ...fontStyle, maxWidth: props.fullInfo ? '200px' : '240px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.data.message}</ISTableCell>
                    {props.adminInfo &&
                      <ISTableCell align="center" style={fontStyle}
                      //onClick={(e) => { e.stopPropagation(); props.onChangeData && !row.data.dateClose && props.onChangeData(row.data, true, false, false) }}
                      >
                        {row.data.dateClose && row.data.closeReason ? t(complaintCloseReasonToDescription(row.data.closeReason)) :
                          row.data.dateClose ? t('closed') : t(complaintStatusTypeToDescription(row.data.status))}
                      </ISTableCell>}
                    {!props.adminInfo && <ISTableCell align="center" tooltip={tooltipTitle} style={fontStyle}>
                      {row.data.dateClose && row.data.closeReason ? t(complaintCloseReasonToDescription(row.data.closeReason)) :
                        row.data.dateClose ? t('closed') : t(complaintStatusTypeToDescription(row.data.status))}</ISTableCell>}
                    {props.fullInfo &&
                      <ISTableCell style={fontStyle}>
                        {row.data.fileId_1 ?
                          <Tooltip title={row.data.fileName_1 ? tooltipText(row.data.fileName_1) : tooltipTitle}>
                            <IconButton color="secondary" sx={{ padding: "0px"}}
                              onClick={(e) => { e.stopPropagation(); row.data.fileId_1 && row.data.fileName_1 && downloadFileComplaintMessage(row.data, row.data.fileId_1, row.data.fileName_1) }}>
                              {downloading === `${row.data.id}${row.data.fileId_1}` ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                            </IconButton>
                          </Tooltip> : ''}

                        {row.data.fileName_2 && row.data.fileId_2 ?
                          <Tooltip title={row.data.fileName_2 ? tooltipText(row.data.fileName_2) : tooltipTitle}>
                            <IconButton color="secondary" sx={{ padding: "0px"}}
                              onClick={(e) => { e.stopPropagation(); row.data.fileId_2 && row.data.fileName_2 && downloadFileComplaintMessage(row.data, row.data.fileId_2, row.data.fileName_2) }}>
                              {downloading === `${row.data.id}${row.data.fileId_2}` ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                            </IconButton>
                          </Tooltip> : ''}
                        {row.data.fileName_3 && row.data.fileId_3 ?
                          <Tooltip title={row.data.fileName_3 ? tooltipText(row.data.fileName_3) : tooltipTitle}>
                            <IconButton color="secondary" sx={{ padding: "0px"}}
                              onClick={(e) => { e.stopPropagation(); row.data.fileId_3 && row.data.fileName_3 && downloadFileComplaintMessage(row.data, row.data.fileId_3, row.data.fileName_3) }}>
                              {downloading === `${row.data.id}${row.data.fileId_3}` ? <CircularProgress size={15} /> : <AttachFile fontSize="small" />}
                            </IconButton>
                          </Tooltip> : ''}
                      </ISTableCell>
                    }
                    {props.fullInfo && <ISTableCell align="center" tooltip={tooltipTitle} style={fontStyle}>{row.data.dateClose ? moment(new Date(row.data.dateClose)).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY') : t('active')}</ISTableCell>}

                    {props.staffInfo && !row.data.visibleCategory &&
                      <ISTableCell tooltip={row.data.internalAssignedToName ? tooltipTitle : tooltipText(t("assignYourself"))} style={fontStyle}
                        onClick={(e) => { e.stopPropagation(); row.data.internalAssignedToName ? props.onClickInfo && props.onClickInfo(row.data) : props.onChangeData && props.onChangeData(row.data, false, false, true) }}>{row.data.internalAssignedToName ? row.data.internalAssignedToName : t('notAssigned')}</ISTableCell>}
                    {props.adminInfo && (!props.staffInfo || row.data.visibleCategory) && <ISTableCell tooltip={row.data.assignedToName ? tooltipTitle : !props.managerInfo ? tooltipText(t("assignYourself")) : ''} style={fontStyle}
                      onClick={(e) => { e.stopPropagation(); row.data.assignedToName ? props.onClickInfo && props.onClickInfo(row.data) : props.onChangeData && !props.managerInfo && props.onChangeData(row.data, false, true, false) }}>{row.data.assignedToName ? row.data.assignedToName : t('notAssigned')}</ISTableCell>}
                    {props.adminInfo && <ISTableCell tooltip={otherTooltip(row.data.nombrefantasia)}
                      style={{ ...fontStyle, maxWidth: props.fullInfo ? '180px' : '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.data.nombrefantasia}
                    </ISTableCell>}

                    {props.fullInfo &&
                      ((!props.staffInfo && row.data.assignedToId) || (props.staffInfo && row.data.visibleCategory && row.data.assignedToId)) ?
                      <TableCell align='center'>
                        <Badge badgeContent={unreadMessages} color="primary">
                          <ISTableButton text={props.isClient ? t('tracking') : t('client')}
                            onClick={(e) => { e.stopPropagation(); props.onClickTracking && props.onClickTracking(row.data) }}>
                          </ISTableButton>
                        </Badge>
                      </TableCell> : props.fullInfo ? <TableCell align='center'></TableCell> : '' }
                    {/*props.fullInfo && (((props.staffInfo || props.adminInfo) && !row.data.assignedToId) || (props.staffInfo && !row.data.visibleCategory)) && 
                    <TableCell align='center'></TableCell>*/}
                    {props.fullInfo && props.adminInfo && !row.data.visibleCategory && !props.isChaco && 
                      ((!props.staffInfo && row.data.assignedToId) || (props.staffInfo && row.data.internalAssignedToId > 0)) ?
                      <TableCell align='center'>
                        <Badge badgeContent={unreadInternalMessages} color="primary">
                          <ISTableButton text={props.adminInfo && !props.staffInfo ? t('logistics') : t('sales')} // t('internalTracking')}
                            onClick={(e) => { e.stopPropagation(); props.onClickInternalTracking && props.onClickInternalTracking(row.data) }}>
                          </ISTableButton>
                        </Badge>
                      </TableCell>
                       : !props.isClient ? props.fullInfo && !props.isChaco ? <TableCell align='center'></TableCell> : '' : ''}
                    {/*
                    {props.fullInfo && props.adminInfo && row.data.visibleCategory && <TableCell align='center'></TableCell>}
                    {props.fullInfo && props.adminInfo && !row.data.visibleCategory && (((!props.staffInfo && !row.data.assignedToId) || (props.staffInfo)) && (row.data.internalAssignedToId === -1 || row.data.internalAssignedToId === null)) && <TableCell align='center'></TableCell>}
                    */}
                  </ISTableRow>
                )
              })}
            </TableBody>
          </Table>
          {props.loading &&
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={25} />
            </div>
          }
          {props.fullInfo && <PaginationButtons pagination={props.pagination} changePage={props.onChangePage} />}
        </TableContainer>
      </Collapse>
    </Stack>
  );
}

export default Complaints
